import { FunctionComponent, memo } from "react";
import logo from "images/icon-colour-light-background.svg";
import Page from "constants/page";
import SignOut from "./sign-out";
import VerifyPermission from "components-for/pages/verify-permission";
import { CategoryNavigation, ClientNavigation, NavigationLink } from "@edgetier/client-components";
import {
    faBolt,
    faBellOn,
    faCog,
    faBinoculars,
    faChartBar,
    faSpellCheck,
    faUsersCog,
} from "@fortawesome/pro-solid-svg-icons";
import { faTags, faTag, faUsers, faUser, faUserTag, faUserEdit, faUserCog } from "@fortawesome/pro-solid-svg-icons";
import { IClient, IUser, Permission } from "@edgetier/watchtower-types";
import { getDefaultRouteUrls } from "utilities/get-default-route-urls";
import { IProps } from "./navigation.types";
import {
    MANAGE_AGENT_TAGS_ENABLED,
    SPELLING_EXEMPTIONS_SCREEN_ENABLED,
    TAG_COMPARISON_SCREEN_ENABLED,
} from "constants/feature-flags";
import VerifyManageTagsPermission from "components-for/tags/verify-manage-tags-permission";
import { useUser } from "hooks-for/users/use-user";
import ClientList from "./client-list";
import useUsersClients from "hooks-for/users/use-users-clients";
import ClientDetails from "./client-details";
import { SpinnerUntil } from "@edgetier/components";
import "./navigation.scss";

/**
 * Sidebar with links to other pages.
 * @param props.isAgentReviewEnabled
 * @param props.isExploreTagsEnabled
 */
const Navigation: FunctionComponent<IProps> = ({ isAgentReviewEnabled, isExploreTagsEnabled }) => {
    const defaultRouteUrls = getDefaultRouteUrls();
    const { data: user, isLoading: isUserLoading } = useUser();
    const { data: userClients, isLoading: isUserClientsLoading, isError: isUserClientsError } = useUsersClients();

    return (
        <ClientNavigation logo={logo}>
            <SpinnerUntil<{ user: IUser; userClients: IClient[] }>
                data={{ user, userClients }}
                isReady={!isUserLoading && !isUserClientsLoading}
            >
                {(data) => (
                    <>
                        <VerifyPermission permission={Permission.ViewAgent}>
                            <CategoryNavigation icon={faUser} label="Agent Review">
                                {isAgentReviewEnabled && (
                                    <VerifyPermission permission={Permission.ViewAgent}>
                                        <NavigationLink
                                            label="Agent"
                                            icon={faUser}
                                            page={defaultRouteUrls[Page.Agent].url}
                                        />
                                    </VerifyPermission>
                                )}

                                {isAgentReviewEnabled && (
                                    <VerifyPermission permission={Permission.ViewAgent}>
                                        <NavigationLink
                                            label="Agents"
                                            icon={faUsers}
                                            page={defaultRouteUrls[Page.Agents].url}
                                        />
                                    </VerifyPermission>
                                )}
                            </CategoryNavigation>
                        </VerifyPermission>

                        <VerifyPermission permission={Permission.ViewAnomalies}>
                            <NavigationLink label="Anomalies" icon={faBellOn} page={Page.Anomalies} />
                        </VerifyPermission>

                        <VerifyPermission
                            permission={[Permission.ViewExplore, Permission.ViewLive, Permission.ViewCustomReports]}
                        >
                            <CategoryNavigation icon={faBinoculars} label="Explore">
                                <VerifyPermission permission={Permission.ViewExplore}>
                                    <NavigationLink
                                        label="Explore"
                                        icon={faBinoculars}
                                        page={defaultRouteUrls[Page.Explore].url}
                                    />
                                </VerifyPermission>

                                {TAG_COMPARISON_SCREEN_ENABLED && isExploreTagsEnabled && (
                                    <VerifyPermission permission={Permission.ViewExplore}>
                                        <NavigationLink
                                            label="Explore Tags"
                                            icon={faTag}
                                            page={defaultRouteUrls[Page.ExploreTags].url}
                                        />
                                    </VerifyPermission>
                                )}

                                <VerifyPermission permission={Permission.ViewLive}>
                                    <NavigationLink label="Live" icon={faBolt} page={defaultRouteUrls[Page.Live].url} />
                                </VerifyPermission>
                                <VerifyPermission permission={Permission.ViewCustomReports}>
                                    <NavigationLink
                                        label="Custom Reports"
                                        icon={faChartBar}
                                        page={Page.CustomReports}
                                    />
                                </VerifyPermission>
                            </CategoryNavigation>
                        </VerifyPermission>

                        <VerifyPermission
                            permission={[
                                Permission.ManagePhraseTriggers,
                                Permission.ManageInteractionTags,
                                Permission.ManageWritingIssues,
                                Permission.ManageAgentTags,
                            ]}
                        >
                            <CategoryNavigation icon={faCog} label="Configuration">
                                <VerifyManageTagsPermission>
                                    <NavigationLink label="Tags &amp; Triggers" icon={faTags} page={Page.ManageTags} />
                                </VerifyManageTagsPermission>
                                {MANAGE_AGENT_TAGS_ENABLED && isAgentReviewEnabled && (
                                    <VerifyPermission permission={Permission.ManageAgentTags}>
                                        <NavigationLink
                                            label="Agent Groups"
                                            icon={faUsersCog}
                                            page={Page.AgentGroups}
                                        />
                                    </VerifyPermission>
                                )}
                                {SPELLING_EXEMPTIONS_SCREEN_ENABLED && (
                                    <VerifyPermission permission={Permission.ManageWritingIssues}>
                                        <NavigationLink
                                            label="Spelling Exemptions"
                                            icon={faSpellCheck}
                                            page={Page.SpellingConfiguration}
                                        />
                                    </VerifyPermission>
                                )}
                            </CategoryNavigation>
                        </VerifyPermission>

                        <VerifyPermission permission={Permission.ManageUsers}>
                            <CategoryNavigation icon={faUsers} label="User Management">
                                <VerifyPermission permission={Permission.ManageUsers}>
                                    <NavigationLink label="Permissions" icon={faUserTag} page={Page.Permissions} />
                                </VerifyPermission>

                                <VerifyPermission permission={Permission.ManageUsers}>
                                    <NavigationLink label="Users" icon={faUserEdit} page={Page.Users} />
                                </VerifyPermission>
                            </CategoryNavigation>
                        </VerifyPermission>

                        <CategoryNavigation
                            icon={faUserCog}
                            label={`${user?.firstName} ${user?.surname}` ?? "User"}
                            placement="right-end"
                            className="user-details"
                        >
                            {Array.isArray(userClients) && userClients.length >= 2 && <ClientList />}
                            <SignOut />
                        </CategoryNavigation>

                        {isUserClientsError ? (
                            <li className="navigation-link navigation-link__error">
                                <div className="navigation-link__inner">Could not load client details</div>
                            </li>
                        ) : (
                            <>{data.userClients.length >= 2 && <ClientDetails clientId={user?.clientId!} />}</>
                        )}
                    </>
                )}
            </SpinnerUntil>
        </ClientNavigation>
    );
};

export default memo(Navigation);
